import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CalendarClock, Trash2, Calculator, Info } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { brl, numBR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";

const EMPTY = {
  customer_name: "", aparelhos: 5, horas_por_aparelho: 1, visitas_ano: 12,
  km_por_visita: 20, pmoc_anual: 0, rt_anual: 0, taxa_anual: 0, outros_anual: 0,
};

export default function Contratos() {
  const [form, setForm] = useState(EMPTY);
  const [calc, setCalc] = useState(null);
  const [items, setItems] = useState([]);
  const [gate, setGate] = useState(null);
  const [saving, setSaving] = useState(false);

  const num = numBR;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = () => api.get("/contracts").then((r) => setItems(r.data)).catch((e) => {
    const msg = apiError(e);
    if (msg.includes("planos Profissional")) setGate("plan");
    else if (msg.includes("custos operacionais")) setGate("costs");
  });
  useEffect(() => { load(); }, []);

  const doCalc = async () => {
    try {
      const payload = { ...form, aparelhos: num(form.aparelhos) || 1, visitas_ano: num(form.visitas_ano) || 12 };
      const { data } = await api.post("/contracts/calculate", payload);
      setCalc(data);
    } catch (e) {
      const msg = apiError(e);
      if (msg.includes("custos operacionais")) { setGate("costs"); return; }
      if (msg.includes("planos Profissional")) { setGate("plan"); return; }
      toast.error(msg);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, aparelhos: num(form.aparelhos) || 1, visitas_ano: num(form.visitas_ano) || 12 };
      await api.post("/contracts", payload);
      toast.success("Contrato salvo!");
      setCalc(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este contrato?")) return;
    await api.delete(`/contracts/${id}`);
    load();
  };

  if (gate === "plan") {
    return (
      <div className="max-w-xl mx-auto space-y-4 animate-in-up" data-testid="contracts-gate-plan">
        <h1 className="text-2xl font-bold tracking-tight">Contratos de manutenção mensal</h1>
        <Card className="border-sky-200 bg-sky-50"><CardContent className="p-5 space-y-3">
          <p className="text-sm text-sky-900">A precificação de contratos recorrentes (PMOC, RT, taxas diluídas em 12 meses) é exclusiva dos planos <b>Profissional</b> e <b>Empresa</b>.</p>
          <Link to="/app/planos"><Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="contracts-gate-upgrade">Ver planos</Button></Link>
        </CardContent></Card>
      </div>
    );
  }
  if (gate === "costs") {
    return (
      <div className="max-w-xl mx-auto space-y-4 animate-in-up" data-testid="contracts-gate-costs">
        <h1 className="text-2xl font-bold tracking-tight">Contratos de manutenção mensal</h1>
        <Card className="border-amber-200 bg-amber-50"><CardContent className="p-5 space-y-3">
          <p className="text-sm text-amber-900">Configure seus custos operacionais primeiro — o contrato usa sua hora técnica e seu custo por km reais.</p>
          <Link to="/app/custos"><Button className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" data-testid="contracts-gate-custos">Configurar meus custos</Button></Link>
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-5 animate-in-up max-w-3xl" data-testid="contracts-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><CalendarClock className="h-6 w-6 text-sky-600" /> Contratos de manutenção mensal</h1>
        <p className="text-sm text-slate-500">Precifique contratos recorrentes: todos os custos da operação diluídos em 12 meses — mão de obra, deslocamento, PMOC, documentação RT e taxa anual.</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          <div className="space-y-1.5"><Label>Cliente (opcional)</Label><Input className="h-12" data-testid="ct-customer" value={form.customer_name} onChange={set("customer_name")} placeholder="Ex: Supermercado Bom Preço" /></div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label>Qtd. de aparelhos</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" min="1" data-testid="ct-aparelhos" value={form.aparelhos} onChange={set("aparelhos")} /></div>
            <div className="space-y-1.5"><Label>Horas por aparelho/visita</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.5" data-testid="ct-horas" value={form.horas_por_aparelho} onChange={set("horas_por_aparelho")} /></div>
            <div className="space-y-1.5"><Label>Visitas por ano</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" min="1" data-testid="ct-visitas" value={form.visitas_ano} onChange={set("visitas_ano")} /></div>
            <div className="space-y-1.5"><Label>Km por visita (ida+volta)</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.1" data-testid="ct-km" value={form.km_por_visita} onChange={set("km_por_visita")} /></div>
            <div className="space-y-1.5"><Label>PMOC (R$/ano)</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.01" data-testid="ct-pmoc" value={form.pmoc_anual} onChange={set("pmoc_anual")} /></div>
            <div className="space-y-1.5"><Label>Documentação RT (R$/ano)</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.01" data-testid="ct-rt" value={form.rt_anual} onChange={set("rt_anual")} /></div>
            <div className="space-y-1.5"><Label>Taxa anual (R$/ano)</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.01" data-testid="ct-taxa" value={form.taxa_anual} onChange={set("taxa_anual")} /></div>
            <div className="space-y-1.5"><Label>Outros custos (R$/ano)</Label><Input className="h-12 font-mono-num" type="text" inputMode="decimal" step="0.01" data-testid="ct-outros" value={form.outros_anual} onChange={set("outros_anual")} /></div>
          </div>
          <p className="text-[11px] text-slate-400 flex gap-1.5"><Info className="h-3.5 w-3.5 shrink-0 mt-0.5" /> A hora técnica, o valor por km, impostos e margem vêm dos seus Meus Custos. O preço é diluído na recorrência de 12 meses.</p>
          <Button type="button" className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white" onClick={doCalc} data-testid="ct-calc-btn">
            <Calculator className="h-4 w-4 mr-2" /> Calcular contrato
          </Button>
        </CardContent>
      </Card>

      {calc && (
        <Card className="border-sky-200 shadow-sm" data-testid="ct-result">
          <CardContent className="p-4 space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Formação do contrato (anual)</p>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Mão de obra ({calc.visitas_ano} visitas × {calc.aparelhos} aparelhos)</span><span className="font-mono-num" data-testid="ct-mo">{brl(calc.custo_mao_obra_ano)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">Deslocamento no ano</span><span className="font-mono-num" data-testid="ct-desl">{brl(calc.custo_deslocamento_ano)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">PMOC + RT + taxas + outros</span><span className="font-mono-num" data-testid="ct-docs">{brl(calc.custos_documentacao_ano)}</span></div>
              <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold"><span>Custo total anual</span><span className="font-mono-num" data-testid="ct-custo-total">{brl(calc.custo_total_ano)}</span></div>
            </div>
            <div className="bg-slate-900 text-white rounded-lg p-4 grid grid-cols-3 gap-2 text-center">
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Mensalidade</p>
                <p className="font-mono-num text-xl font-bold" data-testid="ct-mensal">{brl(calc.mensal)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Por aparelho/mês</p>
                <p className="font-mono-num text-xl font-bold" data-testid="ct-por-aparelho">{brl(calc.por_aparelho_mes)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400">Mínimo mensal</p>
                <p className="font-mono-num text-xl font-bold" data-testid="ct-minimo">{brl(calc.minimo_mensal)}</p>
              </div>
            </div>
            <p className="text-[11px] text-slate-500">Mínimo mensal = cobre custos + impostos, sem margem. Abaixo dele o contrato dá prejuízo. Impostos {calc.impostos_pct}% · margem {calc.margem_pct}% · hora base {brl(calc.valor_hora_base)} · km base {brl(calc.valor_km_base)}. Você define o preço final.</p>
            <Button type="button" className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={save} disabled={saving} data-testid="ct-save-btn">
              {saving ? "Salvando..." : "Salvar contrato"}
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-3">
        {items.map((c) => (
          <Card key={c.id} className="border-slate-200 shadow-sm" data-testid={`ct-card-${c.id}`}>
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold truncate">{c.customer_name || "Contrato"} · {c.calc?.aparelhos} aparelhos</p>
                <p className="text-sm text-slate-500">{c.calc?.visitas_ano} visitas/ano · <span className="font-mono-num font-semibold text-slate-700">{brl(c.calc?.mensal || 0)}/mês</span> · por aparelho {brl(c.calc?.por_aparelho_mes || 0)}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">{c.status}</Badge>
                <Button variant="ghost" size="icon" className="h-10 w-10 text-red-500 hover:bg-red-50" onClick={() => remove(c.id)} data-testid={`ct-del-${c.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
